import { showAppAlert } from '../../common/appDialog';
import React, { useState } from 'react';
import { ICONS } from '../constants';
import { ReviewDialog, SrmTargetDialog } from './AnalysisModals';
import { useAnalysisPersistence } from '../../hooks/useAnalysisPersistence';
import { printRenderedReport } from '../../utils/printRenderedReport';
import { SrmModule, SrmProject } from '@types';
import { ReportActionsProvider } from './ReportActionsContext';
import { reportSaveScope } from '../../common/analysisPersistence';

interface AnalysisReportShellProps {
    title: string;
    toolbarTitle?: string;
    subtitle: string;
    report: any;
    children: React.ReactNode;
    organizationId?: string;
    clientId?: string | null;
    srmProject?: SrmProject;
    srmModule?: any;
    onCustomSave?: () => Promise<void>;
    customSaveLabel?: string;
    showReviewButton?: boolean;
    onNavigate?: (nav: any) => void;
    onRegenerate?: () => void;
    // G5 FIX: Back navigation from report view back to the suite workflow list
    onBack?: () => void;
    backLabel?: string;
    preferredWorkflowId?: string;
    readOnly?: boolean;
}

/**
 * Standardized shell for all intelligence reports.
 * Centralizes the header, action buttons, and persistence modals.
 */
export const AnalysisReportShell: React.FC<AnalysisReportShellProps> = ({
    title,
    toolbarTitle,
    subtitle,
    report,
    children,
    organizationId = '',
    clientId = null,
    srmProject,
    srmModule,
    onCustomSave,
    customSaveLabel,
    showReviewButton = false,
    onNavigate,
    onRegenerate,
    // G5 FIX
    onBack,
    backLabel = 'Back to Suite',
    preferredWorkflowId,
    readOnly = false,
}) => {
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showSrmTargetModal, setShowSrmTargetModal] = useState(false);
    const [availableSrmProjects, setAvailableSrmProjects] = useState<SrmProject[]>([]);
    const [srmTargetError, setSrmTargetError] = useState('');
    const [loadingTargets, setLoadingTargets] = useState(false);

    const scope = {
        ...reportSaveScope(report),
        organizationId: organizationId || srmProject?.organizationId || report?.organizationId || '',
        clientId: clientId || srmProject?.clientId || report?.clientId || null,
        assetId: srmProject?.assetId || report?.assetId,
        srmProjectId: srmProject?.id || report?.srmProjectId,
        srmModuleId: srmModule?.id || report?.srmModuleId,
        workflowId: preferredWorkflowId || reportSaveScope(report).workflowId || srmModule?.workflowId,
    };
    const scopedReport = { ...report, ...scope,
        title: report?.title || (report?.groupName ? `Threat Group Intelligence: ${report.groupName}` : title) };

    const {
        isSaving,
        isReviewing,
        handleSaveToLibrary,
        handleSaveToSrm,
        handleSendForReview,
        getActiveSrmProjects
    } = useAnalysisPersistence();

    const handleDownload = () => {
        printRenderedReport(title);
    };

    const handleSaveClick = async () => {
        if (onCustomSave) {
            await onCustomSave();
            return;
        }
        const result = await handleSaveToLibrary(scopedReport, scope.organizationId, scope.clientId);
        if (result.success)
            void showAppAlert(result.existing === 'library' ? 'This report is already saved in Intelligence Library.'
                : result.existing === 'srm' ? `This report is already saved in SRM${result.location ? `: ${result.location}` : ''}.`
                : 'Report saved to Intelligence Library.');
        else
            void showAppAlert(result.error);
    };

    const attachReportToSrm = async (project: SrmProject, module: SrmModule) => {
        const success = await handleSaveToSrm(
            scopedReport,
            project.id,
            module.id,
            scope
        );
        if (success)
            setShowSrmTargetModal(false);
    };

    const onSaveToSrmConfirm = async () => {
        if (loadingTargets || isSaving)
            return;
        setLoadingTargets(true);
        setAvailableSrmProjects([]);
        setSrmTargetError('');
        try {
            if (!scope.organizationId)
                throw new Error('Organization context is missing. Reopen this report and retry.');
            if (!scope.assetId && !scope.srmProjectId)
                throw new Error('This report has no linked facility. Open it from the facility SRM framework.');
            const projects = await getActiveSrmProjects(scope);
            if (!projects.length)
                throw new Error('No SRM framework with a matching report module exists for this facility.');
            const preferred = projects.find(project => project.id === scope.srmProjectId);
            const target = preferred || (projects.length === 1 ? projects[0] : undefined);
            const module = target?.modules.find(module => module.id === scope.srmModuleId)
                || (target?.modules.length === 1 ? target.modules[0] : undefined);
            if (target && module) {
                await attachReportToSrm(target, module);
                return;
            }
            setAvailableSrmProjects(projects);
            setShowSrmTargetModal(true);
        } catch (error) {
            console.error('Error loading SRM projects:', error);
            setSrmTargetError(error instanceof Error ? error.message : 'Active SRM projects could not be loaded.');
            setShowSrmTargetModal(true);
        } finally {
            setLoadingTargets(false);
        }
    };

    const onReviewSubmit = async (email: string, notes: string) => {
        const success = await handleSendForReview(report, email, notes, organizationId);
        if (success) setShowReviewModal(false);
    };

    return (
        <div className="analysis-report-print-area space-y-6">
            {/* G5 FIX: Back button always visible when a callback is provided */}
            {onBack && (
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-blue-600 transition-colors group"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {backLabel}
                </button>
            )}
            <div className={`flex justify-between items-center border-b border-slate-200 pb-4 mb-4 bg-white p-4 rounded-lg border shadow-sm ${toolbarTitle ? 'flex-wrap gap-4' : ''}`}>
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">{toolbarTitle || title}</h1>
                    <p className="text-sm text-slate-500">{subtitle}</p>
                </div>
                <div className={`flex gap-2 ${toolbarTitle ? 'shrink-0' : ''}`}>
                    {!readOnly && showReviewButton && (
                        <button 
                            onClick={() => setShowReviewModal(true)} 
                            className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            Submit for Review
                        </button>
                    )}

                    {!readOnly && onRegenerate && (
                        <button 
                            onClick={onRegenerate}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm"
                            title="Regenerate this intelligence report"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            Regenerate
                        </button>
                    )}

                    {!readOnly && (!onCustomSave || srmProject && srmModule) && (
                        <button 
                            onClick={onSaveToSrmConfirm}
                            disabled={isSaving || loadingTargets}
                            className="bg-amber-600 hover:bg-amber-700 disabled:bg-slate-400 text-white font-bold py-2 px-4 rounded-md flex items-center transition-all text-sm shadow-lg border-b-2 border-amber-700 active:translate-y-0.5"
                            title="Map this result to your SRM Framework"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{loadingTargets ? 'Finding SRM...' : 'Save to SRM'}</span>
                        </button>
                    )}

                    {!readOnly && <button
                        onClick={handleSaveClick} 
                        disabled={isSaving || loadingTargets}
                        className={`${onCustomSave ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-green-600 hover:bg-green-700'} disabled:bg-slate-400 text-white font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm`}
                    >
                        {isSaving ? 'Saving...' : (customSaveLabel || "Save")}
                    </button>}

                    <button 
                        onClick={handleDownload} 
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-md flex items-center transition-colors text-sm"
                    >
                        {React.cloneElement(ICONS.download as any, {className: 'h-4 w-4 mr-2'})}
                        <span>Print / Save PDF</span>
                    </button>
                </div>
            </div>

            {!readOnly && showReviewModal && (
                <ReviewDialog
                    onClose={() => setShowReviewModal(false)}
                    onSubmit={onReviewSubmit}
                    isSubmitting={isReviewing}
                />
            )}

            {!readOnly && showSrmTargetModal && (
                <SrmTargetDialog
                    projects={availableSrmProjects}
                    preferredProjectId={scope.srmProjectId}
                    preferredModuleId={scope.srmModuleId}
                    error={srmTargetError}
                    isSaving={isSaving}
                    onClose={() => setShowSrmTargetModal(false)}
                    onSubmit={attachReportToSrm}
                />
            )}

            <div className="report-content">
                <ReportActionsProvider value={{
                    onSave: handleSaveClick,
                    onDownloadPdf: handleDownload,
                    isSaving,
                    hasCustomSave: Boolean(onCustomSave),
                    customSaveLabel,
                    readOnly,
                }}>
                    {children}
                </ReportActionsProvider>
            </div>
        </div>
    );
};

export default AnalysisReportShell;
